import React from 'react';
import classNames from 'classnames';

import {
  withStyles,
  createStyles,
  WithStyles,
  Theme,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Box from '@material-ui/core/Box';

import Title from '../../Common/Title';
import { IWidgetAdapt } from '../../../types/IRestaurantWidgetsStore';
import locales from '../../../locales';
import EditIcon from '../../Common/Icons/EditIcon';
import TrashIcon from '../../Common/Icons/TrashIcon';
import CopyIcon from '../../Common/Icons/CopyIcon';
import { WidgetsSettings } from "./WidgetsSettings";

const styles = (theme: Theme) => createStyles({
  wrapper: {
    maxWidth: '100%'
  },
  // Десктопная версия
  desktopPaper: {
    [theme.breakpoints.down(960)]: {
      display: 'none',
    },
  },
  link: {
    padding: 8,
    backgroundColor: '#f7f8fb',
    borderRadius: 5,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  pL: {
    paddingLeft: 24,
  },
  bntWrap: {
    padding: '10px 24px 24px 24px',
  },
  icon: {
    cursor: 'pointer',
  },
  trashIcon: {
    marginLeft: 12,
    cursor: 'pointer',
  },
  deleteBorder: {
    borderBottom: 'none',
  },
  firstCln: {
    width: '30%',
  },
  secondCln: {
    width: '60%',
  },
  thirdCln: {
    paddingRight: 24,
    width: '10%',
  },
  tableWrapper: {
    overflow: "auto",
    maxWidth: "100%"
  },
  table: {
    minWidth: "500px"
  },
  // Мобильная версия
  mobileCard: {
    display: 'none',
    [theme.breakpoints.down(960)]: {
      display: 'block',
    },
  },
  card: {
    borderRadius: 8,
    border: '1px solid #E5E5E5',
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 0,
  },
  cardContent: {
    padding: theme.spacing(2),
    width: '100%',
    boxSizing: 'border-box',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
  },
  cardContentHeader: {
    display: 'flex',
    justifyContent: "space-between",
    alignItems: 'center'
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: 0,
  },
  cardLink: {
    padding: 8,
    backgroundColor: '#f7f8fb',
    borderRadius: 5,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    wordBreak: 'break-word',
  },
  cardLinkText: {
    fontSize: 14,
    color: theme.palette.text.primary,
    flex: 1,
    marginRight: theme.spacing(1),
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  mobileTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
  },
  mobileSubtitle: {
    fontSize: 14,
    color: '#AAAAAA',
    marginBottom: theme.spacing(3),
  },
  mobileWrapper: {
    padding: theme.spacing(2),
  },
  mobileButtonWrap: {
    paddingTop: theme.spacing(2),
  },
});

interface Props extends WithStyles<typeof styles> {
  widgets: Array<IWidgetAdapt>;
  handleOpenWidgetModal: (type: 'create' | 'edit' | 'delete', widgetInfo?: IWidgetAdapt) => void;
  handleCustomMsg: () => void;
}

// TODO Поправить стили
const widgetsTable: React.FC<Props> = ({
  classes,
  widgets,
  handleOpenWidgetModal,
  handleCustomMsg,
}) => {
  const copyLink = (link: string) => {
    handleCustomMsg();
    navigator.clipboard.writeText(link);
  };

  return (
    <>
      {/* Десктопная версия (таблица) */}
      <Paper elevation={3} className={classNames(classes.wrapper, classes.desktopPaper)}>
        <Title
          text={locales.t('settings.widgets.title')}
          subTitle={locales.t('settings.widgets.desc')}
        />
        <WidgetsSettings />
        <div className={classes.tableWrapper}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell className={classNames(classes.pL, classes.deleteBorder, classes.firstCln)}>
                  { locales.t('settings.widgets.table.name') }
                </TableCell>
                <TableCell className={classNames(classes.deleteBorder, classes.secondCln)}>
                  { locales.t('settings.widgets.table.link') }
                </TableCell>
                <TableCell className={classNames(classes.deleteBorder, classes.thirdCln)}>
                  { locales.t('settings.widgets.table.action') }
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {
                widgets.map(w => (
                  <TableRow key={w.name}>
                    <TableCell className={classNames(classes.pL, classes.deleteBorder)} style={{ maxWidth: '0rem' }}>
                      <Typography noWrap style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        { w.name }
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.deleteBorder}>
                      <div className={classes.link}>
                        { w.url }
                        <CopyIcon
                          className={classes.icon}
                          onClick={() => copyLink(w.url)}
                        />
                      </div>
                    </TableCell>
                    <TableCell className={classes.deleteBorder}>
                      <EditIcon
                        className={classes.icon}
                        onClick={() => handleOpenWidgetModal('edit', w)}
                      />
                      <TrashIcon
                        className={classes.trashIcon}
                        onClick={() => handleOpenWidgetModal('delete', w)}
                      />
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        </div>
        <div className={classes.bntWrap}>
          <Button
            onClick={() => handleOpenWidgetModal('create')}
            variant="contained"
            color="primary"
          >
            { locales.t('settings.widgets.btn') }
          </Button>
        </div>
      </Paper>

      {/* Мобильная версия (карточки) */}
      <Card className={classNames(classes.mobileCard)}>
        <CardContent className={classes.cardContent}>
          <Typography className={classes.mobileTitle}>
            {locales.t('settings.widgets.title')}
          </Typography>
          <Typography className={classes.mobileSubtitle}>
            {locales.t('settings.widgets.desc')}
          </Typography>

          {widgets.map(w => (
            <Card key={w.name} className={classes.card} elevation={0}>
              <CardContent className={classes.cardContent}>
                <Box className={classes.cardContentHeader}>
                  <Typography className={classes.cardTitle}>
                    {w.name}
                  </Typography>
                  <Box className={classes.cardActions}>
                    <EditIcon
                      className={classes.icon}
                      onClick={() => handleOpenWidgetModal('edit', w)}
                    />
                    <TrashIcon
                      className={classes.icon}
                      onClick={() => handleOpenWidgetModal('delete', w)}
                    />
                  </Box>
                </Box>
                <div className={classes.cardLink}>
                  <Typography className={classes.cardLinkText}>
                    {w.url}
                  </Typography>
                  <CopyIcon
                    className={classes.icon}
                    onClick={() => copyLink(w.url)}
                  />
                </div>
              </CardContent>
            </Card>
          ))}

          <div className={classes.mobileButtonWrap}>
            <Button
              onClick={() => handleOpenWidgetModal('create')}
              variant="contained"
              color="primary"
              fullWidth
            >
              { locales.t('settings.widgets.btn') }
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default withStyles(styles)(widgetsTable);
